/*
 * The keys of an AI chat's page (S12 §9), registered into the one shortcut registry through the
 * `shortcuts` slot, which is what puts them in the keyboard sheet: an undocumented shortcut cannot
 * exist (`docs/web/keyboard.md`).
 *
 * Two scopes: `aichat` is the page — the density, the details column — and `aichatLog` the transcript's
 * row walk; the page pushes the first while it is on route and the transcript the second while it has
 * the keyboard (`core/shell/shortcuts.ts`, stack `global · aichat · aichatLog · composer`). The message
 * box's own keys — send, add to the running request, edit my last message — are the composer's.
 *
 * A definition is not a behaviour: the registry is built once per boot, while what `j` means depends
 * on the transcript on screen. Each binding calls the controller the open page installs, and with no
 * page open its scope is not on the stack and a key that arrived anyway does nothing. No chord stops a
 * turn or switches the mode: a stray key must never stop a colleague's work or change what Claude may
 * do, and both are one visible press away.
 */

import type { ShortcutDef } from "../../core/modules/registry.ts"
import { COMPOSER_ID } from "../../core/shell/layout.ts"
import { reviewShortcuts } from "./review/keys.ts"

/** What the open chat page lends the page's keys. */
export interface AichatPageController {
	/** `Mod+O`: the next density, written to the reader's setting. */
	cycleDensity(): void
	/** `Mod+.`: shows or hides the details column. */
	toggleDetails(): void
}

/** What the transcript lends the row walk while it has the keyboard. */
export interface AichatLogController {
	/** `Escape`: back to the message box. */
	toComposer(): void
	/** `j` / `k`: the next or previous row. */
	stepRow(dir: 1 | -1): void
	/** `Enter`: opens or closes the row that has the focus. */
	toggleRow(): void
	/** `]` / `[`: the next or previous request. */
	stepRequest(dir: 1 | -1): void
}

let page: AichatPageController | null = null
let log: AichatLogController | null = null

/** Installed by the chat page while it is mounted; cleared when it is not. */
export function setAichatPageController(next: AichatPageController | null): void {
	page = next
}

/** Installed by the transcript while it is mounted; cleared when it is not. */
export function setAichatLogController(next: AichatLogController | null): void {
	log = next
}

/** Puts the focus back into the message box, as Escape in the transcript does: after a card's answer. */
export function focusComposer(): void {
	log?.toComposer()
}

export const aichatShortcuts: ShortcutDef[] = [
	{
		id: "aichat.cycleDensity",
		keys: ["Mod+O"],
		scope: "aichat",
		labelKey: "aichat.shortcut.cycleDensity",
		// Spoken by the page itself: what it says names the density it arrived at.
		run: () => page?.cycleDensity(),
	},
	{
		id: "aichat.toggleDetails",
		// Chat's chord for the same column.
		keys: ["Mod+."],
		scope: "aichat",
		labelKey: "aichat.shortcut.toggleDetails",
		run: () => page?.toggleDetails(),
	},
	{
		// The one door, both ways (S12 §9, chat's): from the box back onto the row the reader left,
		// and from anywhere else — a row, the band — into the message box. The direction is read from
		// where the focus is, as chat's door reads it: the transcript lends its controller for as long
		// as it is mounted, so whether one is installed says nothing about who has the keyboard. It is
		// the page's binding, since the row walk's scope is on the stack only while the rows have the
		// keyboard; a palette or a popover that is open takes Escape first.
		id: "aichat.boxAndRows",
		keys: ["Escape"],
		scope: "aichat",
		labelKey: "aichat.shortcut.boxAndRows",
		run: () => {
			if (document.activeElement?.closest(`#${COMPOSER_ID}`) == null) {
				log?.toComposer()
				return
			}
			// The rows' half comes with the chat page, which is on screen whenever the rows are: the
			// module object — and with it the entry chunk — carries none of the transcript.
			void import("./transcript/focusRows.ts").then(({ focusTranscriptRows }) =>
				focusTranscriptRows(),
			)
		},
	},
	{
		id: "aichat.log.nextRow",
		keys: ["j"],
		scope: "aichatLog",
		labelKey: "aichat.shortcut.nextRow",
		run: () => log?.stepRow(1),
	},
	{
		id: "aichat.log.previousRow",
		keys: ["k"],
		scope: "aichatLog",
		labelKey: "aichat.shortcut.previousRow",
		run: () => log?.stepRow(-1),
	},
	{
		id: "aichat.log.toggleRow",
		keys: ["Enter"],
		scope: "aichatLog",
		labelKey: "aichat.shortcut.toggleRow",
		run: () => log?.toggleRow(),
	},
	{
		id: "aichat.log.nextRequest",
		keys: ["]"],
		scope: "aichatLog",
		labelKey: "aichat.shortcut.nextRequest",
		run: () => log?.stepRequest(1),
	},
	{
		id: "aichat.log.previousRequest",
		keys: ["["],
		scope: "aichatLog",
		labelKey: "aichat.shortcut.previousRequest",
		run: () => log?.stepRequest(-1),
	},
	// The review's Code layer (`review/keys.ts`), in the `diff` scope.
	...reviewShortcuts,
]
